import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorText, qs, type Paged } from '../api';
import { useAdmin } from '../auth';
import { ColumnChart, fmtNum } from '../charts';
import { Icon } from '../icons';
import type { Analytics } from './analytics';
import { Btn, Empty, ErrorBox, Loading, Modal, Pager, REJECT_REASONS, REPORT_REASONS, STATUS_RU, fmtDate, useAction } from '../ui';

export const ACTION_RU: Record<string, string> = {
  submitted: 'отправлено', resubmitted: 'отправлено повторно', auto_published: 'автопубликация', approved: 'одобрено',
  rejected: 'отклонено', taken_down: 'снято', edited_resubmit: 'изменено → повторная проверка', expired: 'истёк срок',
};

interface ListingRow {
  id: string;
  title: string;
  status: string;
  price: { formatted: string };
  category: string | null;
  location: string | null;
  thumbUrl: string | null;
  submittedAt: string | null;
  flags: ListingFlags | null;
  openReports: number;
  user: { id: string; firstName: string; username: string | null; status: string };
}

export interface DashboardStats {
  users: { total: number; newLast7Days: number; blocked: number };
  listings: { byStatus: Record<string, number>; submittedLast7Days: number };
  openReports: number;
  failedNotificationsLast7Days: number;
  deletionRequests: { pending: number; overdue: number; responseDays: number };
  businessApplications: number;
  /** Отзывы с жалобами (ждут решения модератора). */
  reportedReviews?: number;
}

export function Dashboard() {
  const { can } = useAdmin();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['stats'], queryFn: () => api<DashboardStats>('/admin/stats') });
  const week = useQuery({ queryKey: ['analytics', 7], queryFn: () => api<Analytics>('/admin/analytics?days=7') });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorBox error={error} onRetry={() => void refetch()} />;
  const pending = data.listings.byStatus.pending ?? 0;
  const attention = [
    { to: '/queue', label: 'Объявления ждут проверки', value: pending, icon: 'bolt', perm: 'listings.moderate' },
    { to: '/reports', label: 'Открытые жалобы', value: data.openReports, icon: 'flag', perm: 'reports.view' },
    { to: '/business', label: 'Заявки на бизнес-профиль', value: data.businessApplications, icon: 'briefcase', perm: 'business.manage' },
    { to: '/deletion-requests', label: 'Запросы на удаление данных', value: data.deletionRequests.pending, icon: 'trash', perm: 'deletion_requests.process' },
  ].filter((x) => can(x.perm));
  const tiles = [
    ['Пользователи', data.users.total, `+${fmtNum(data.users.newLast7Days)} за 7 дней`],
    ['Активные объявления', data.listings.byStatus.active ?? 0, `подано за 7 дней: ${fmtNum(data.listings.submittedLast7Days)}`],
    ['На модерации', pending, 'ждут решения'],
    ['Открытые жалобы', data.openReports, 'раздел «Жалобы»'],
    ['Заблокированы', data.users.blocked, 'пользователей'],
    ['Недоставленные уведомления (7 дн.)', data.failedNotificationsLast7Days, 'бот заблокирован пользователем и т. п.'],
  ] as const;
  return (
    <div>
      <div className="page-head">
        <h1>Сводка</h1>
        {can('stats.view') ? (
          <Link to="/analytics" className="btn btn-secondary">
            <Icon name="chart" size={16} /> Аналитика
          </Link>
        ) : null}
      </div>
      {data.deletionRequests.overdue > 0 ? (
        <p className="pill pill-danger" role="alert">
          Просрочено запросов на удаление данных: {data.deletionRequests.overdue} (срок ответа {data.deletionRequests.responseDays} дн.) — раздел «Удаление данных»
        </p>
      ) : null}
      <div className="tiles">
        {tiles.map(([label, value, hint]) => (
          <div key={label} className={`tile ${label === 'На модерации' && value > 0 ? 'tile-attention' : ''}`}>
            <span className="tile-label">{label}</span>
            <span className="tile-value">{fmtNum(value)}</span>
            <span className="tile-delta">{hint}</span>
          </div>
        ))}
      </div>
      <div className="panels">
        <section className="panel" aria-label="Требует внимания">
          <h2>Требует внимания</h2>
          <p className="sub">Открыть раздел и разобрать</p>
          <div className="attention">
            {attention.map((x) => (
              <Link key={x.to} to={x.to}>
                <Icon name={x.icon} />
                {x.label}
                <b>{fmtNum(x.value)}</b>
              </Link>
            ))}
          </div>
        </section>
        <section className="panel" aria-label="Неделя">
          <h2>Последние 7 дней</h2>
          <p className="sub">Новые пользователи по дням</p>
          {week.data ? <ColumnChart rows={week.data.daily} series={[{ key: 'newUsers', label: 'Новые' }]} height={150} ariaLabel="Новые пользователи за неделю" /> : week.error ? <ErrorBox error={week.error} /> : <Loading />}
        </section>
      </div>
    </div>
  );
}

export function ListingsPage({ defaultStatus = 'pending' }: { defaultStatus?: string }) {
  const { can } = useAdmin();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(defaultStatus);
  const [q, setQ] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(params.get('open'));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkReason, setBulkReason] = useState('other');
  const [bulk, setBulk] = useState<{ done: number; total: number; errors: string[] } | null>(null);
  // Переход «#123» из быстрого поиска открывает объявление.
  useEffect(() => {
    const id = params.get('open');
    if (id && /^\d{1,19}$/.test(id)) setOpen(id);
  }, [params]);
  useEffect(() => setSelected(new Set()), [status, q, flagged, page]);
  const closeReview = () => {
    setOpen(null);
    if (params.get('open')) setParams({}, { replace: true });
  };
  const bulkMode = status === 'pending' && can('listings.moderate');
  const runBulk = async (kind: 'approve' | 'reject') => {
    const ids = [...selected];
    setBulk({ done: 0, total: ids.length, errors: [] });
    const errors: string[] = [];
    for (let i = 0; i < ids.length; i++) {
      try {
        await api(`/admin/listings/${ids[i]}/${kind}`, { method: 'POST', json: kind === 'approve' ? {} : { reasonCode: bulkReason } });
      } catch (e) {
        errors.push(`#${ids[i]}: ${errorText(e)}`);
      }
      setBulk({ done: i + 1, total: ids.length, errors: [...errors] });
    }
    setSelected(new Set());
    void qc.invalidateQueries({ queryKey: ['admin-listings'] });
    void qc.invalidateQueries({ queryKey: ['stats'] });
  };
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-listings', status, q, flagged, page],
    queryFn: () => api<Paged<ListingRow>>(`/admin/listings${qs({ status, q, page, pageSize: 20, flagged: flagged ? 'true' : undefined })}`),
  });
  return (
    <div>
      <div className="page-head">
        <h1>{status === 'pending' ? 'Очередь модерации' : 'Объявления'}</h1>
        {status === 'pending' && can('listings.moderate') ? (
          <Link to="/queue" className="btn btn-primary">
            <Icon name="bolt" size={16} /> Быстрая модерация
          </Link>
        ) : null}
      </div>
      <div className="toolbar">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {['pending', 'active', 'rejected', 'archived', 'draft', 'deleted'].map((s) => (
            <option key={s} value={s}>{STATUS_RU[s]}</option>
          ))}
        </select>
        <input placeholder="Поиск по тексту" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <label className="check">
          <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} /> Только со стоп-словами
        </label>
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty /> : (
        <>
          <table>
            <thead>
              <tr>
                {bulkMode ? (
                  <th className="check-col">
                    <input
                      type="checkbox"
                      aria-label="Выбрать все на странице"
                      checked={data.items.length > 0 && data.items.every((l) => selected.has(l.id))}
                      onChange={(e) => setSelected(e.target.checked ? new Set(data.items.map((l) => l.id)) : new Set())}
                    />
                  </th>
                ) : null}
                <th /><th>Объявление</th><th>Категория / место</th><th>Продавец</th><th>Подано</th><th>Сигналы</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id} className={`clickable ${selected.has(l.id) ? 'selected' : ''}`} onClick={() => setOpen(l.id)}>
                  {bulkMode ? (
                    <td className="check-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Выбрать #${l.id}`}
                        checked={selected.has(l.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(l.id);
                          else next.delete(l.id);
                          setSelected(next);
                        }}
                      />
                    </td>
                  ) : null}
                  <td>{l.thumbUrl ? <img className="thumb" src={l.thumbUrl} alt="" /> : <div className="thumb" />}</td>
                  <td><strong>{l.title || '(без названия)'}</strong><div className="muted">#{l.id} · {l.price.formatted}</div></td>
                  <td>{l.category}<div className="muted">{l.location}</div></td>
                  <td>{l.user.firstName}{l.user.username ? <div className="muted">@{l.user.username}</div> : null}{l.user.status !== 'active' ? <span className="pill pill-danger">{STATUS_RU[l.user.status]}</span> : null}</td>
                  <td>{fmtDate(l.submittedAt)}</td>
                  <td>
                    <FlagPills flags={l.flags} />
                    {l.openReports ? <span className="pill pill-danger">жалоб: {l.openReports}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={data.total} onPage={setPage} />
          {bulkMode && (selected.size > 0 || bulk) ? (
            <div className="bulk-bar" role="region" aria-label="Действия с выбранными">
              {bulk && bulk.done < bulk.total ? (
                <span>Обработано {bulk.done} из {bulk.total}…</span>
              ) : (
                <>
                  <span>Выбрано: {selected.size}</span>
                  <Btn disabled={!selected.size} onClick={() => void runBulk('approve')}>
                    <Icon name="check" size={16} /> Одобрить
                  </Btn>
                  <select value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} aria-label="Причина отклонения">
                    {Object.entries(REJECT_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <Btn kind="danger" disabled={!selected.size} onClick={() => void runBulk('reject')}>
                    <Icon name="x" size={16} /> Отклонить
                  </Btn>
                  {bulk ? (
                    <span>
                      Готово: {bulk.total - bulk.errors.length} из {bulk.total}
                      {bulk.errors.length ? ` · ошибки: ${bulk.errors.join('; ')}` : ''}
                    </span>
                  ) : null}
                  <button type="button" className="link" style={{ color: '#bff0dc' }} onClick={() => { setSelected(new Set()); setBulk(null); }}>
                    Снять выбор
                  </button>
                </>
              )}
            </div>
          ) : null}
        </>
      )}
      {open ? <ListingReview id={open} onClose={closeReview} /> : null}
    </div>
  );
}

export function ListingReview({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAdmin();
  const action = useAction();
  const [reason, setReason] = useState('other');
  const [text, setText] = useState('');
  const { data: l, isLoading, error } = useQuery({
    queryKey: ['admin-listing', id],
    queryFn: () =>
      api<{
        id: string; status: string; title: string; description: string; price: { formatted: string }; condition: string | null;
        category: string | null; location: string | null; flags: ListingFlags | null; rejection: { code: string | null; text: string | null };
        images: { id: string; url: string }[]; attributes: { key: string; label: string; value: unknown }[];
        user: { id: string; firstName: string; username: string | null; status: string };
        history: { action: string; from: string | null; to: string | null; reasonCode: string | null; reasonText: string | null; note: string | null; admin: string | null; at: string }[];
        reports: { id: string; reason: string; comment: string | null; status: string; at: string }[];
        signals?: ModerationSignals;
      }>(`/admin/listings/${id}`),
  });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['admin-listings'] });
    void qc.invalidateQueries({ queryKey: ['admin-listing', id] });
    void qc.invalidateQueries({ queryKey: ['stats'] });
  };
  const decide = (kind: 'approve' | 'reject' | 'takedown') =>
    action.run(async () => {
      await api(`/admin/listings/${id}/${kind}`, { method: 'POST', json: kind === 'approve' ? {} : { reasonCode: reason, reasonText: text.trim() || undefined } });
      done();
      onClose();
    });

  return (
    <Modal title={`Объявление #${id}`} onClose={onClose}>
      {isLoading ? <Loading /> : error || !l ? <ErrorBox error={error} /> : (
        <div className="review">
          <div className="gallery">{l.images.map((i) => <a key={i.id} href={i.url} target="_blank" rel="noreferrer noopener"><img src={i.url} alt="" /></a>)}</div>
          <p><span className="pill">{STATUS_RU[l.status]}</span> {l.category} · {l.location} <FlagPills flags={l.flags} /></p>
          <SignalList flags={null} signals={l.signals} />
          <h2>{l.title}</h2>
          <p className="price">{l.price.formatted}</p>
          {/* Пользовательский текст выводится как текст (React экранирует), без HTML. */}
          <p className="pre">{l.description}</p>
          {l.attributes.length ? <ul>{l.attributes.map((a) => <li key={a.key}>{a.label}: <strong>{String(a.value)}</strong></li>)}</ul> : null}
          <p>Продавец: {l.user.firstName} {l.user.username ? `@${l.user.username}` : ''} (#{l.user.id}, {STATUS_RU[l.user.status] ?? l.user.status})</p>
          {l.reports.length ? (
            <>
              <h3>Жалобы</h3>
              <ul>{l.reports.map((r) => <li key={r.id}>{REPORT_REASONS[r.reason]} — {r.comment ?? ''} <span className="muted">({STATUS_RU[r.status]}, {fmtDate(r.at)})</span></li>)}</ul>
            </>
          ) : null}
          <h3>История модерации</h3>
          <ul className="muted">{l.history.map((h, i) => <li key={i}>{fmtDate(h.at)} — {ACTION_RU[h.action] ?? h.action} {h.from ? `${h.from}→${h.to}` : ''} {h.reasonCode ? `(${REJECT_REASONS[h.reasonCode] ?? h.reasonCode}${h.reasonText ? `: ${h.reasonText}` : ''})` : ''} {h.admin ? `· ${h.admin}` : ''} {h.note ?? ''}</li>)}</ul>

          {can('listings.moderate') && (l.status === 'pending' || l.status === 'active') ? (
            <div className="decision">
              {l.status === 'pending' ? <Btn busy={action.busy} onClick={() => void decide('approve')}>Одобрить</Btn> : null}
              <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Причина">
                {Object.entries(REJECT_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input placeholder="Комментарий для пользователя (необязательно)" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
              <Btn kind="danger" busy={action.busy} onClick={() => void decide(l.status === 'pending' ? 'reject' : 'takedown')}>
                {l.status === 'pending' ? 'Отклонить' : 'Снять с публикации'}
              </Btn>
            </div>
          ) : null}
          {action.error ? <ErrorBox error={action.error} /> : null}
        </div>
      )}
    </Modal>
  );
}

interface ReportRow {
  id: string; reason: string; comment: string | null; status: string; createdAt: string;
  reporter: { id: string; firstName: string };
  listing: { id: string; title: string; status: string; sellerId: string } | null;
  seller: { id: string; firstName: string; status: string } | null;
  resolutionNote: string | null; resolvedBy: string | null; resolvedAt: string | null;
  conversationId?: string | null;
}

interface ReportConversation {
  id: string;
  listing: { id: string; title: string };
  reporterIsBuyer: boolean;
  messages: { id: string; author: string; fromReporter: boolean; text: string; createdAt: string }[];
}

/** Переписка из чата, приложенная к жалобе. Каждый просмотр пишется в журнал аудита (сервер). */
function ConversationView({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['report-conversation', reportId], queryFn: () => api<ReportConversation>(`/admin/reports/${reportId}/conversation`) });
  return (
    <Modal title={`Переписка по жалобе #${reportId}`} onClose={onClose}>
      <p className="muted small">Просмотр записан в журнал. Используйте только для рассмотрения жалобы.</p>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} /> : data ? (
        <>
          <p>Объявление: #{data.listing.id} {data.listing.title}</p>
          <div className="conversation" data-testid="report-conversation">
            {data.messages.map((m) => (
              <div key={m.id} className={`conv-msg ${m.fromReporter ? 'conv-reporter' : ''}`}>
                <div className="small muted">{m.author} · {fmtDate(m.createdAt)}{m.fromReporter ? ' · автор жалобы' : ''}</div>
                <div className="pre">{m.text}</div>
              </div>
            ))}
            {!data.messages.length ? <Empty text="Сообщений нет" /> : null}
          </div>
        </>
      ) : null}
    </Modal>
  );
}

export function ReportsPage() {
  const qc = useQueryClient();
  const { can } = useAdmin();
  const [status, setStatus] = useState('open');
  const [page, setPage] = useState(1);
  const [openListing, setOpenListing] = useState<string | null>(null);
  const [resolving, setResolving] = useState<ReportRow | null>(null);
  const [conversation, setConversation] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', status, page],
    queryFn: () => api<Paged<ReportRow>>(`/admin/reports${qs({ status, page, pageSize: 20 })}`),
  });
  return (
    <div>
      <h1>Жалобы</h1>
      <div className="toolbar">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          {['open', 'resolved', 'dismissed'].map((s) => <option key={s} value={s}>{STATUS_RU[s]}</option>)}
        </select>
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty text="Жалоб нет" /> : (
        <>
          <table>
            <thead><tr><th>Причина</th><th>Объект</th><th>Автор</th><th>Дата</th><th>Решение</th><th /></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td><strong>{REPORT_REASONS[r.reason]}</strong><div className="muted pre">{r.comment}</div></td>
                  <td>
                    {r.listing ? <button className="link" onClick={() => setOpenListing(r.listing!.id)}>#{r.listing.id} {r.listing.title}</button> : r.seller ? `${r.conversationId ? 'Собеседник' : 'Продавец'} ${r.seller.firstName} (#${r.seller.id})` : '—'}
                    {r.conversationId ? <div><button className="link" onClick={() => setConversation(r.id)}>Показать переписку</button></div> : null}
                  </td>
                  <td>{r.reporter.firstName} #{r.reporter.id}</td>
                  <td>{fmtDate(r.createdAt)}</td>
                  <td>{r.resolvedBy ? `${r.resolvedBy}: ${r.resolutionNote ?? ''}` : '—'}</td>
                  <td>{r.status === 'open' && can('reports.resolve') ? <Btn kind="secondary" onClick={() => setResolving(r)}>Рассмотреть</Btn> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={data.total} onPage={setPage} />
        </>
      )}
      {openListing ? <ListingReview id={openListing} onClose={() => setOpenListing(null)} /> : null}
      {conversation ? <ConversationView reportId={conversation} onClose={() => setConversation(null)} /> : null}
      {resolving ? <ResolveReport report={resolving} onClose={() => { setResolving(null); void qc.invalidateQueries({ queryKey: ['reports'] }); }} /> : null}
    </div>
  );
}

function ResolveReport({ report, onClose }: { report: ReportRow; onClose: () => void }) {
  const action = useAction();
  const [note, setNote] = useState('');
  const [act, setAct] = useState<'none' | 'takedown_listing' | 'block_user'>('none');
  const [reasonCode, setReasonCode] = useState('other');
  return (
    <Modal title={`Жалоба #${report.id}`} onClose={onClose}>
      <p>{REPORT_REASONS[report.reason]}: {report.comment}</p>
      <label className="field">Внутренний комментарий
        <textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
      </label>
      <label className="field">Действие
        <select value={act} onChange={(e) => setAct(e.target.value as typeof act)}>
          <option value="none">Без действий (нарушение не подтверждено полностью)</option>
          {report.listing ? <option value="takedown_listing">Снять/отклонить объявление</option> : null}
          <option value="block_user">Заблокировать продавца</option>
        </select>
      </label>
      {act === 'takedown_listing' ? (
        <label className="field">Причина для пользователя
          <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
            {Object.entries(REJECT_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      ) : null}
      {act === 'block_user' ? <p className="warn">Блокировка — только после проверки. Основание будет записано в журнал.</p> : null}
      <div className="row">
        <Btn busy={action.busy} onClick={() => void action.run(async () => { await api(`/admin/reports/${report.id}/resolve`, { method: 'POST', json: { note: note || undefined, action: act, reasonCode } }); onClose(); })}>Подтвердить нарушение</Btn>
        <Btn kind="secondary" busy={action.busy} onClick={() => void action.run(async () => { await api(`/admin/reports/${report.id}/dismiss`, { method: 'POST', json: { note: note || undefined } }); onClose(); })}>Отклонить жалобу</Btn>
      </div>
      {action.error ? <ErrorBox error={action.error} /> : null}
    </Modal>
  );
}

export interface ListingFlags {
  bannedWords?: string[];
  /** Похожее объявление того же продавца (pg_trgm similarity ≥ 0,75) — возможный дубликат. */
  similarTo?: { id: string; score: number } | null;
  /** Фото совпадает с фото в объявлении другого продавца (перцептивный хеш) — возможная кража фото. */
  photoReusedFrom?: { listingId: string; userId: string; distance: number } | null;
}

/** Автоматические пометки при публикации. Это подсказки модератору, а не решение. */
export function FlagPills({ flags }: { flags: ListingFlags | null | undefined }) {
  if (!flags) return null;
  return (
    <>
      {flags.bannedWords?.length ? <span className="pill pill-warn">стоп-слова: {flags.bannedWords.join(', ')}</span> : null}
      {flags.similarTo ? (
        <span className="pill pill-warn" title="Похожее объявление этого же продавца">
          похоже на #{flags.similarTo.id} ({Math.round(flags.similarTo.score * 100)}%)
        </span>
      ) : null}
      {flags.photoReusedFrom ? (
        <span className="pill pill-danger" title="Такое же фото есть в объявлении другого продавца: проверьте, не скопированы ли фото">
          фото как в #{flags.photoReusedFrom.listingId} (другой продавец)
        </span>
      ) : null}
    </>
  );
}

export interface ModerationSignals {
  seller: { accountAgeDays: number; listings: number; active: number; rejected: number; openReports: number; confirmedReports: number } | null;
  price: { verdict: 'low' | 'high' | 'normal' | null; medianSum: number | null; samples: number };
}

/** Подсказки модератору: стоп-слова, дубликаты, фото, продавец, цена. Это не решение — только повод присмотреться. */
export function signalItems(flags: ListingFlags | null | undefined, s: ModerationSignals | undefined): { level: 'ok' | 'warn' | 'danger'; text: string }[] {
  const out: { level: 'ok' | 'warn' | 'danger'; text: string }[] = [];
  if (flags?.bannedWords?.length) out.push({ level: 'danger', text: `Стоп-слова: ${flags.bannedWords.join(', ')}` });
  if (flags?.photoReusedFrom) out.push({ level: 'danger', text: `Фото как в объявлении #${flags.photoReusedFrom.listingId} другого продавца — возможно, фото скопированы` });
  if (flags?.similarTo) out.push({ level: 'warn', text: `Похоже на #${flags.similarTo.id} этого же продавца (${Math.round(flags.similarTo.score * 100)}%) — возможный дубликат` });
  const sel = s?.seller;
  if (sel) {
    if (sel.confirmedReports > 0) out.push({ level: 'danger', text: `На продавца уже подтверждались жалобы: ${sel.confirmedReports}` });
    if (sel.openReports > 0) out.push({ level: 'danger', text: `Открытых жалоб на продавца: ${sel.openReports}` });
    if (sel.rejected > 0) out.push({ level: 'warn', text: `Раньше отклоняли объявления продавца: ${sel.rejected}` });
    if (sel.accountAgeDays < 2) out.push({ level: 'warn', text: `Новый продавец: аккаунту ${sel.accountAgeDays === 0 ? 'меньше суток' : '1 день'}` });
  }
  const p = s?.price;
  if (p?.verdict === 'low') out.push({ level: 'danger', text: `Цена намного ниже обычной для категории (медиана ${fmtNum(p.medianSum ?? 0)} сум) — частый признак мошенничества` });
  if (p?.verdict === 'high') out.push({ level: 'warn', text: `Цена намного выше обычной (медиана ${fmtNum(p.medianSum ?? 0)} сум) — возможно, ошибка в нулях` });
  if (!out.length && s) {
    out.push({ level: 'ok', text: sel ? `Замечаний нет. Продавец на площадке ${sel.accountAgeDays} дн., объявлений ${sel.listings}` : 'Замечаний нет' });
  }
  return out;
}

export function SignalList({ flags, signals }: { flags: ListingFlags | null | undefined; signals: ModerationSignals | undefined }) {
  const items = signalItems(flags, signals);
  if (!items.length) return null;
  return (
    <div className="signals" aria-label="Подсказки модератору">
      {items.map((i) => (
        <div key={i.text} className={`signal ${i.level}`}>
          <Icon name={i.level === 'ok' ? 'check' : 'flag'} size={16} />
          {i.text}
        </div>
      ))}
    </div>
  );
}
